import React from 'react';
import { Divider, Field, Input, Select, Text } from '@fluentui/react-components';
import { useForm } from 'react-hook-form';
import { useAppActions, useAppState } from '../../context';
import { getValidationState } from './components/formValidation';
import { isPageReferenceActivityGroup, PageReference } from '@gkn-ili/views/dist/OrderDocumentation';
import { AppEditDialogActions } from './components/dialogActions';
import {
  AppDialogBody,
  AppDialogContent,
  AppDialogHeader,
  AppDialogSurface,
  DialogOptions,
} from './components/baseComponents';
import { useLargeModalStyles as useLargeModalStyles } from './components/commonStyles';

type FormState = { name: string; description: string; previousAG: string };

export default function AppCreateSubStepModal(props: {
  initialPreviousReference: PageReference;
  dialogOptions: DialogOptions;
}): JSX.Element {
  const styles = useLargeModalStyles();
  const appState = useAppState();
  const appActions = useAppActions();
  const routing = appState.orderView.routing[props.initialPreviousReference.routingItemKey];
  const step = routing.steps[props.initialPreviousReference.stepKey];

  const formHandlers = useForm<FormState>({
    defaultValues: {
      name: 'New activity group',
      description: '',
      previousAG: isPageReferenceActivityGroup(props.initialPreviousReference)
        ? props.initialPreviousReference.activityGroupKey
        : 'none',
    },
  });
  const onSubmit = formHandlers.handleSubmit(async (data) => {
    await appActions.editOrder.createActivityGroup(
      props.initialPreviousReference,
      data.name,
      data.description,
      data.previousAG,
    );
    props.dialogOptions.onCloseDialog();
  });

  return (
    <AppDialogSurface dialogOptions={props.dialogOptions}>
      <form>
        <AppDialogBody dialogOptions={props.dialogOptions}>
          <AppDialogHeader dialogOptions={props.dialogOptions}>Create activity group</AppDialogHeader>
          <AppDialogContent dialogOptions={props.dialogOptions}>
            <Text>Edit the activity group details and click save to create the step.</Text>
            <Field label="Activity group name" required {...getValidationState('name', formHandlers.formState.errors)}>
              <Input {...formHandlers.register('name', { required: true })} name="name" className={styles.field} />
            </Field>
            <Field
              label="Activity group description"
              {...getValidationState('description', formHandlers.formState.errors)}
            >
              <Input {...formHandlers.register('description')} className={styles.field} />
            </Field>
            <Divider>Organization</Divider>
            <Field
              label="Comes after activity group"
              required
              {...getValidationState('previousAG', formHandlers.formState.errors)}
            >
              <Select {...formHandlers.register('previousAG', { required: true })}>
                <option value="none">As first</option>
                {Object.entries(step.activityGroups).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value.name}
                  </option>
                ))}
              </Select>
            </Field>
          </AppDialogContent>
          <AppEditDialogActions
            onCancel={props.dialogOptions.onCloseDialog}
            submitDisabled={formHandlers.formState.isSubmitting}
            onSubmit={onSubmit}
          />
        </AppDialogBody>
      </form>
    </AppDialogSurface>
  );
}
